import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Divider,
    Chip,
} from '@mui/material';
import {
    CloudUpload as CloudUploadIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Save as SaveIcon,
} from '@mui/icons-material';
import { PageHeader } from '../../components/admin';
import { useToast } from '../../components/toast/useToast';
import {
    useHydrantyQuery,
    useHydrantyInfoQuery,
    useUploadHydrantyMutation,
    useUploadHydrantyGpxMutation,
    useDeleteHydrantyMutation,
} from '../../api/JsdhNotifikaceService/service/HydrantyMutations';
import type { HydrantyGeoJSON } from '../../api/JsdhNotifikaceService/generated';
import HydrantyMapPreview from '../../components/HydrantyMapPreview';

const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const HydrantyPage: React.FC = () => {
    const { showToast } = useToast();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const [localGeojson, setLocalGeojson] = useState<HydrantyGeoJSON | null>(null);
    const [hasChanges, setHasChanges] = useState(false);

    const { data: hydrantyInfo, isLoading: isLoadingInfo, refetch: refetchInfo } = useHydrantyInfoQuery();
    // Always fetch hydrants - the query handles 404 gracefully
    const { data: hydrantyData, isLoading: isLoadingData, refetch: refetchData } = useHydrantyQuery(true);

    // Sync local state with server data
    useEffect(() => {
        if (hydrantyData) {
            setLocalGeojson(JSON.parse(JSON.stringify(hydrantyData)));
            setHasChanges(false);
        }
    }, [hydrantyData]);
    const uploadMutation = useUploadHydrantyMutation();
    const uploadGpxMutation = useUploadHydrantyGpxMutation();
    const deleteMutation = useDeleteHydrantyMutation();

    const isUploading = uploadMutation.isPending || uploadGpxMutation.isPending;

    const validateGeoJSON = (content: string): HydrantyGeoJSON | null => {
        try {
            const parsed = JSON.parse(content);

            // Validate structure
            if (parsed.type !== 'FeatureCollection') {
                throw new Error('GeoJSON musí být typu FeatureCollection');
            }

            if (!Array.isArray(parsed.features)) {
                throw new Error('GeoJSON musí obsahovat pole features');
            }

            // Validate features
            for (let i = 0; i < parsed.features.length; i++) {
                const feature = parsed.features[i];
                if (feature.type !== 'Feature') {
                    throw new Error(`Prvek ${i + 1} musí být typu Feature`);
                }
                if (!feature.geometry || feature.geometry.type !== 'Point') {
                    throw new Error(`Prvek ${i + 1} musí mít geometrii typu Point`);
                }
                if (!Array.isArray(feature.geometry.coordinates) ||
                    feature.geometry.coordinates.length < 2) {
                    throw new Error(`Prvek ${i + 1}: Souřadnice musí být pole [longitude, latitude]`);
                }
            }

            return parsed;
        } catch (error) {
            if (error instanceof SyntaxError) {
                showToast('Soubor neobsahuje platný JSON.', 'error');
            } else if (error instanceof Error) {
                showToast(error.message, 'error');
            }
            return null;
        }
    };

    const validateGpx = (content: string): boolean => {
        // Basic GPX validation - check for XML structure and wpt elements
        if (!content.includes('<gpx') || !content.includes('</gpx>')) {
            showToast('Soubor neobsahuje platný GPX formát.', 'error');
            return false;
        }
        if (!content.includes('<wpt')) {
            showToast('GPX soubor neobsahuje žádné waypoint (<wpt>) elementy.', 'error');
            return false;
        }
        return true;
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file size
        if (file.size > MAX_FILE_SIZE_BYTES) {
            showToast(`Soubor je příliš velký. Maximum je ${MAX_FILE_SIZE_MB} MB.`, 'error');
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
        }

        const fileName = file.name.toLowerCase();
        const isGpx = fileName.endsWith('.gpx');
        const isGeoJson = fileName.endsWith('.geojson') || fileName.endsWith('.json');

        if (!isGpx && !isGeoJson) {
            showToast('Povolené formáty jsou .geojson, .json a .gpx', 'error');
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
        }

        setIsValidating(true);
        try {
            const content = await file.text();

            if (isGpx) {
                // GPX upload
                if (validateGpx(content)) {
                    uploadGpxMutation.mutate({
                        gpx: content,
                        nazev: file.name,
                    }, {
                        onSuccess: () => {
                            refetchInfo();
                            refetchData();
                        }
                    });
                }
            } else {
                // GeoJSON upload
                const validatedGeoJSON = validateGeoJSON(content);
                if (validatedGeoJSON) {
                    uploadMutation.mutate({
                        geojson: content,
                        nazev: file.name,
                    }, {
                        onSuccess: () => {
                            refetchInfo();
                            refetchData();
                        }
                    });
                }
            }
        } catch (error) {
            showToast('Nepodařilo se přečíst soubor.', 'error');
        } finally {
            setIsValidating(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleDelete = () => {
        deleteMutation.mutate(undefined, {
            onSuccess: () => {
                setDeleteDialogOpen(false);
                refetchInfo();
                refetchData();
            },
        });
    };

    const handleRefresh = () => {
        refetchInfo();
        refetchData();
    };

    const handleHydrantEdit = (index: number, type: string, status: string) => {
        if (!localGeojson) return;

        const updatedGeojson = JSON.parse(JSON.stringify(localGeojson)) as HydrantyGeoJSON;
        if (updatedGeojson.features[index]) {
            updatedGeojson.features[index].properties = {
                ...updatedGeojson.features[index].properties,
                type: type || undefined,
                status: status || undefined,
            };
            setLocalGeojson(updatedGeojson);
            setHasChanges(true);
        }
    };

    const handleSaveChanges = () => {
        if (!localGeojson || !hasChanges) return;

        uploadMutation.mutate({
            geojson: JSON.stringify(localGeojson),
            nazev: 'Upraveno',
        }, {
            onSuccess: () => {
                setHasChanges(false);
                refetchInfo();
                refetchData();
            }
        });
    };

    const handleDiscardChanges = () => {
        if (hydrantyData) {
            setLocalGeojson(JSON.parse(JSON.stringify(hydrantyData)));
            setHasChanges(false);
        }
    };

    const formatDate = (dateString: string | null | undefined) => {
        if (!dateString) return 'Nikdy';
        return new Date(dateString).toLocaleString('cs-CZ');
    };

    if (isLoadingInfo) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <PageHeader
                title="Hydranty"
                action={
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={handleRefresh}
                    >
                        Obnovit
                    </Button>
                }
            />

            {/* Info Section */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Informace o datech
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Box display="flex" flexDirection="column" gap={2}>
                    <Box display="flex" alignItems="center" gap={2}>
                        <Typography variant="body1" color="text.secondary" sx={{ minWidth: 150 }}>
                            Stav:
                        </Typography>
                        {hydrantyInfo?.maData ? (
                            <Chip
                                icon={<CheckCircleIcon />}
                                label="Data jsou k dispozici"
                                color="success"
                                variant="outlined"
                            />
                        ) : (
                            <Chip
                                icon={<CancelIcon />}
                                label="Žádná data"
                                color="warning"
                                variant="outlined"
                            />
                        )}
                    </Box>

                    <Box display="flex" alignItems="center" gap={2}>
                        <Typography variant="body1" color="text.secondary" sx={{ minWidth: 150 }}>
                            Počet hydrantů:
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                            {hydrantyInfo?.pocetHydrantu ?? 0}
                        </Typography>
                    </Box>

                    {hydrantyInfo?.nazev && (
                        <Box display="flex" alignItems="center" gap={2}>
                            <Typography variant="body1" color="text.secondary" sx={{ minWidth: 150 }}>
                                Název souboru:
                            </Typography>
                            <Typography variant="body1">
                                {hydrantyInfo.nazev}
                            </Typography>
                        </Box>
                    )}

                    <Box display="flex" alignItems="center" gap={2}>
                        <Typography variant="body1" color="text.secondary" sx={{ minWidth: 150 }}>
                            Poslední aktualizace:
                        </Typography>
                        <Typography variant="body1">
                            {formatDate(hydrantyInfo?.casAktualizace)}
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            {/* Map Preview Section */}
            {hydrantyInfo?.maData && (
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="h6">
                            Náhled na mapě
                        </Typography>
                        {hasChanges && (
                            <Box display="flex" gap={1}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={handleDiscardChanges}
                                    disabled={uploadMutation.isPending}
                                >
                                    Zahodit změny
                                </Button>
                                <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={uploadMutation.isPending ?
                                        <CircularProgress size={16} color="inherit" /> :
                                        <SaveIcon />
                                    }
                                    onClick={handleSaveChanges}
                                    disabled={uploadMutation.isPending}
                                >
                                    {uploadMutation.isPending ? 'Ukládám...' : 'Uložit změny'}
                                </Button>
                            </Box>
                        )}
                    </Box>
                    <Divider sx={{ mb: 2 }} />

                    {hasChanges && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            Máte neuloženou úpravu. Kliknutím na "Uložit změny" odešlete změny na server.
                        </Alert>
                    )}

                    {isLoadingData ? (
                        <Box display="flex" justifyContent="center" alignItems="center" height={500}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <HydrantyMapPreview
                            geojson={localGeojson}
                            height={500}
                            editable
                            onEdit={handleHydrantEdit}
                        />
                    )}
                </Paper>
            )}

            {/* Upload Section */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Nahrát data hydrantů
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Alert severity="info" sx={{ mb: 2 }}>
                    Nahrajte soubor ve formátu GeoJSON (.geojson, .json) nebo GPX (.gpx).
                    <br />
                    <strong>GeoJSON:</strong> Podporované vlastnosti: name, type, status.
                    <br />
                    <strong>GPX:</strong> Waypoint elementy (&lt;wpt&gt;) s atributy name, desc, type.
                    <br />
                    Maximální velikost souboru: {MAX_FILE_SIZE_MB} MB.
                </Alert>

                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".geojson,.json,.gpx"
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                />

                <Button
                    variant="contained"
                    startIcon={isValidating || isUploading ?
                        <CircularProgress size={20} color="inherit" /> :
                        <CloudUploadIcon />
                    }
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isValidating || isUploading}
                >
                    {isValidating ? 'Validuji...' :
                        isUploading ? 'Nahrávám...' :
                            'Vybrat soubor'}
                </Button>
            </Paper>

            {/* Delete Section */}
            {hydrantyInfo?.maData && (
                <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom color="error">
                        Nebezpečná zóna
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Smazání dat hydrantů je nevratná operace. Na mapě se přestanou zobrazovat hydranty.
                    </Alert>

                    <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => setDeleteDialogOpen(true)}
                        disabled={deleteMutation.isPending}
                    >
                        Smazat všechny hydranty
                    </Button>
                </Paper>
            )}

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Potvrdit smazání</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Opravdu chcete smazat všechna data o hydrantech?
                        Tato akce je nevratná a hydranty se přestanou zobrazovat na mapě.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>
                        Zrušit
                    </Button>
                    <Button
                        onClick={handleDelete}
                        color="error"
                        variant="contained"
                        disabled={deleteMutation.isPending}
                    >
                        {deleteMutation.isPending ? 'Mažu...' : 'Smazat'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default HydrantyPage;
